/**
 * One body row of a data table, with its expansion when it has one.
 *
 * @module components/ui/DataTableRow
 */

import React from "react";

import { IconButton } from "./Button";
import type { Column, RowExpansion } from "./DataTable";
import { EditLockExempt } from "./EditLock";
import type { RowVirtualProps } from "./VirtualTableBody";

/*
 * One step up from the obvious `sm`/`md`: at 800px the sidebar has already
 * become an overlay and the content column is narrower than the viewport, so a
 * table that only starts shedding columns below 768px is still too wide there.
 */
export const PRIORITY_CELL: Record<NonNullable<Column<unknown>["priority"]>, string> = {
  1: "",
  2: "max-md:w-0 max-md:overflow-hidden max-md:border-x-0 max-md:px-0",
  3: "max-lg:w-0 max-lg:overflow-hidden max-lg:border-x-0 max-lg:px-0",
};

export const PRIORITY_CONTENT: Record<NonNullable<Column<unknown>["priority"]>, string> = {
  1: "contents",
  2: "contents max-md:hidden",
  3: "contents max-lg:hidden",
};

/** What inside a row owns a press, so the row does not also toggle. */
const PRESS_OWNERS =
  "a, button, input, select, textarea, summary, label, [contenteditable]:not([contenteditable='false']), [role='button'], [role='checkbox'], [role='menuitem'], [role='switch'], [role='tab'], [role='link']";

/**
 * Whether a click on a row is a press on the row itself: not on a control
 * inside it, not portaled from a descendant that renders elsewhere, and not
 * the end of a text selection.
 */
function isRowPress(event: React.MouseEvent<HTMLElement>): boolean {
  const target = event.target as HTMLElement;
  return (
    event.currentTarget.contains(target) &&
    target.closest(PRESS_OWNERS) === null &&
    (window.getSelection()?.toString() ?? "") === ""
  );
}

export interface DataTableRowProps<Row> {
  row: Row;
  columns: readonly Column<Row>[];
  rowKey: (row: Row) => string;
  rowTestId: ((row: Row) => string) | undefined;
  rowClassName: ((row: Row) => string) | undefined;
  expansion: RowExpansion<Row> | undefined;
  stickyFirstColumn: boolean;
  rowAlign: string;
  cell: string;
  span: number;
  /** Set by a windowed body, to measure and place the row. */
  virtualProps: RowVirtualProps | undefined;
}

export function DataTableRow<Row>({
  row,
  columns,
  rowKey,
  rowTestId,
  rowClassName,
  expansion,
  stickyFirstColumn,
  rowAlign,
  cell,
  span,
  virtualProps,
}: DataTableRowProps<Row>): React.ReactElement {
  const key = rowKey(row);
  const own = rowTestId?.(row);
  const canExpand = expansion ? (expansion.canExpand?.(row) ?? true) : false;
  const open = canExpand && expansion!.expandedKeys.has(key);
  // Not a suffix of the row's own id: `^=` would then match
  // both, which is the collision an accordion caused once.
  const detailId = own ? `detail-${own}` : `detail-${key}`;
  return (
    <>
      <tr
        {...virtualProps}
        className={`group/row hover:bg-surface-subtle ${canExpand ? "cursor-pointer" : ""} ${rowClassName?.(row) ?? ""}`.trim()}
        // The row is the larger target for the pointer; the
        // chevron stays the one control keyboards and screen
        // readers reach, so a press on anything interactive
        // inside the row is that control's own.
        onClick={
          canExpand
            ? (event) => {
                if (isRowPress(event)) {
                  expansion!.onToggle(key);
                }
              }
            : undefined
        }
        data-testid={own}
      >
        {columns.map((column, index) => {
          const Cell = column.rowHeader ? "th" : "td";
          return (
            <Cell
              key={column.key}
              scope={column.rowHeader ? "row" : undefined}
              className={[
                `border-b border-base-300 ${rowAlign}`,
                cell,
                column.align === "end" ? "text-right" : "",
                PRIORITY_CELL[column.priority ?? 1],
                // The pinned cell paints its own background, so it
                // has to follow the row's hover or the one cell
                // carrying the row's identity never highlights.
                stickyFirstColumn && index === 0
                  ? `sticky left-0 bg-base-100 group-hover/row:bg-surface-subtle`
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <span data-priority-content="" className={PRIORITY_CONTENT[column.priority ?? 1]}>
                {column.render(row)}
              </span>
            </Cell>
          );
        })}
        {expansion && (
          <td className={`w-12 border-b border-base-300 align-middle ${cell}`}>
            {canExpand && (
              <EditLockExempt>
                <IconButton
                  icon={open ? "caret-down" : "caret-right"}
                  aria-label={expansion.label(row)}
                  aria-expanded={open}
                  aria-controls={detailId}
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    expansion.onToggle(key);
                  }}
                  data-testid={expansion.toggleTestId?.(row) ?? (own ? `toggle-${own}` : undefined)}
                />
              </EditLockExempt>
            )}
          </td>
        )}
      </tr>
      {open && (
        <tr id={detailId} data-testid={detailId}>
          <td colSpan={span} className={`border-b border-base-300 ${cell}`}>
            {expansion!.render(row, expansion!.state(row))}
          </td>
        </tr>
      )}
    </>
  );
}
